import { entityOutlineColor, entityOutlineWidth } from "./entity_style";
import type { Camera, RgbColor } from "./types";

export function clearCanvas(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  context.clearRect(0, 0, width, height);
}

export function drawBackgroundGrid(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  camera: Camera,
  arenaWidth = 6400,
  arenaHeight = 6400
): void {
  context.save();
  context.fillStyle = "#f2f2f2";
  context.fillRect(0, 0, width, height);

  const baseSpacing = 38;
  const spacing = Math.max(12, baseSpacing * camera.zoom);
  const offsetX = modulo(-camera.x * camera.zoom, spacing);
  const offsetY = modulo(-camera.y * camera.zoom, spacing);

  context.strokeStyle = "#cdcdcd";
  context.lineWidth = 1.8;

  for (let x = offsetX; x < width; x += spacing) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }

  for (let y = offsetY; y < height; y += spacing) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }

  drawArenaBorder(context, width, height, camera, arenaWidth, arenaHeight);

  context.restore();
}

export function drawHealthBar(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  hp: number,
  maxHp: number
): void {
  const safeMax = Math.max(1, maxHp);
  const ratio = Math.max(0, Math.min(1, hp / safeMax));
  const barHeight = 6;
  const startX = x - width * 0.5;

  context.save();

  context.beginPath();
  context.roundRect(startX, y, width, barHeight, 999);
  context.fillStyle = "rgba(0,0,0,0.18)";
  context.fill();

  context.beginPath();
  context.roundRect(startX, y, width * ratio, barHeight, 999);
  context.fillStyle = ratio > 0.45 ? "#22c55e" : ratio > 0.2 ? "#f59e0b" : "#ef4444";
  context.fill();

  context.restore();
}

export function leaderCrownHeight(size: number): number {
  return size * 1.5;
}

export function drawLeaderCrown(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
): void {
  const fillColor = "#facc15";
  const width = size * 2.25;
  const baseHeight = size * 0.68;
  const peakHeight = size * 0.82;
  const strokeWidth = entityOutlineWidth(size);

  context.save();
  context.translate(x, y);

  buildLeaderCrownPath(context, width, baseHeight, peakHeight);
  context.fillStyle = fillColor;
  context.fill();

  context.save();
  buildLeaderCrownPath(context, width, baseHeight, peakHeight);
  context.clip();

  drawCrownHighlight(
    context,
    -width * 0.37,
    -baseHeight - peakHeight * 0.44,
    size * 0.11,
    size * 0.34,
    -0.12,
    "rgba(255,255,255,0.26)"
  );

  drawCrownHighlight(
    context,
    -width * 0.03,
    -baseHeight - peakHeight * 0.42,
    size * 0.085,
    size * 0.24,
    0.05,
    "rgba(255,255,255,0.2)"
  );

  drawCrownHighlight(
    context,
    width * 0.28,
    -baseHeight - peakHeight * 0.38,
    size * 0.065,
    size * 0.17,
    0.12,
    "rgba(255,255,255,0.16)"
  );

  const shade = context.createLinearGradient(0, -baseHeight - peakHeight, 0, 0);
  shade.addColorStop(0, "rgba(255,255,255,0.04)");
  shade.addColorStop(0.58, "rgba(255,255,255,0)");
  shade.addColorStop(1, "rgba(0,0,0,0.11)");

  context.fillStyle = shade;
  context.fillRect(
    -width * 0.55,
    -baseHeight - peakHeight - strokeWidth,
    width * 1.1,
    baseHeight + peakHeight + strokeWidth
  );

  context.restore();

  buildLeaderCrownPath(context, width, baseHeight, peakHeight);
  context.lineWidth = strokeWidth;
  context.lineJoin = "miter";
  context.lineCap = "butt";
  context.miterLimit = 4;
  context.strokeStyle = entityOutlineColor(fillColor);
  context.stroke();

  context.restore();
}

export function rgbColor(color: RgbColor | undefined, fallback: string): string {
  if (!color) {
    return fallback;
  }

  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

export function heartTierColor(tier: string | null | undefined): string {
  const normalized = String(tier ?? "").toLowerCase();

  if (normalized.includes("platinum") || normalized.includes("200")) {
    return "#67b7ff";
  }

  if (normalized.includes("gold") || normalized.includes("50")) {
    return "#d4af37";
  }

  if (normalized.includes("silver") || normalized.includes("20")) {
    return "#b9c4d0";
  }

  if (normalized.includes("brown") || normalized.includes("5")) {
    return "#8a5a3c";
  }

  return "#ef4444";
}

function drawArenaBorder(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  camera: Camera,
  arenaWidth: number,
  arenaHeight: number
): void {
  const left = width * 0.5 + (-arenaWidth * 0.5 - camera.x) * camera.zoom;
  const top = height * 0.5 + (-arenaHeight * 0.5 - camera.y) * camera.zoom;
  const borderWidth = arenaWidth * camera.zoom;
  const borderHeight = arenaHeight * camera.zoom;

  context.save();
  context.strokeStyle = "rgba(0,0,0,0.45)";
  context.lineWidth = 4;
  context.strokeRect(left, top, borderWidth, borderHeight);

  context.strokeStyle = "rgba(255,255,255,0.55)";
  context.lineWidth = 2;
  context.strokeRect(left + 4, top + 4, borderWidth - 8, borderHeight - 8);
  context.restore();
}

function buildLeaderCrownPath(
  context: CanvasRenderingContext2D,
  width: number,
  baseHeight: number,
  peakHeight: number
): void {
  context.beginPath();
  context.moveTo(-width * 0.5, 0);
  context.lineTo(-width * 0.5, -baseHeight);
  context.lineTo(-width * 0.32, -baseHeight - peakHeight);
  context.lineTo(-width * 0.16, -baseHeight);
  context.lineTo(0, -baseHeight - peakHeight);
  context.lineTo(width * 0.16, -baseHeight);
  context.lineTo(width * 0.32, -baseHeight - peakHeight);
  context.lineTo(width * 0.5, -baseHeight);
  context.lineTo(width * 0.5, 0);
  context.closePath();
}

function drawCrownHighlight(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radiusX: number,
  radiusY: number,
  rotation: number,
  color: string
): void {
  context.beginPath();
  context.ellipse(x, y, radiusX, radiusY, rotation, 0, Math.PI * 2);
  context.fillStyle = color;
  context.fill();
}

function modulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}